import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Bell, Mail, AlertTriangle, Package, Truck, CheckCircle, X,
  ArrowRight, Clock, ExternalLink, Eye
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  message: Mail,
  fulfilment_assigned: Truck,
  stock_update: Package,
  system: AlertTriangle,
  order: Package,
  payment: CheckCircle,
  alert: AlertTriangle,
};

const TYPE_COLORS = {
  message: 'text-[#B99A65]',
  fulfilment_assigned: 'text-teal-600',
  stock_update: 'text-amber-600',
  system: 'text-blue-600',
  order: 'text-emerald-600',
  payment: 'text-emerald-600',
  alert: 'text-red-500',
};

const TYPE_ROUTES = {
  message: '/dashboard/messages',
  fulfilment_assigned: '/dashboard/orders',
  stock_update: '/dashboard/stock',
  order: '/dashboard/orders',
  payment: '/dashboard/orders',
};

// ceo_instruction_ref: notification_deeplink_registry_25sep2026
// ONE shared routing mechanism for every type — used by the bell AND the
// Notifications page. Priority: type registry (exact record from meta) →
// stored link (unless it is the bare dashboard) → generic meta-id scan →
// media reference → null (in-panel detail). NEVER the bare dashboard.
const isDashboardHome = (l) => /^\/dashboard\/?(\?.*)?$/.test(String(l || '').trim());

const enc = (v) => encodeURIComponent(String(v));

// ceo_instruction_ref: region_correct_order_links_27sep2026 — UK records
// open in the UK section only. Nigeria order refs are RK- (internal ord_);
// UK orders are RVK-ORD / SAL-ORD / sord_ / ukor_.
export function orderRoute(refOrId) {
  const v = String(refOrId || '');
  const isNg = /^RK-/i.test(v) || v.startsWith('ord_');
  return isNg
    ? `/dashboard/orders?focus=${enc(v)}`
    : `/dashboard/uk-orders?focus=${enc(v)}`;
}

const TYPE_RECORD_ROUTES = {
  message: (n, m) => resolveMessageRecord(n, m),
  new_registration: (n, m) => (m.registered_user_id ? `/dashboard/salon-pipeline?focus=${enc(m.registered_user_id)}` : null),
  demo_registration: (n, m) => (m.invite_id ? `/dashboard/demo-invites?invite=${enc(m.invite_id)}` : null),
  demo_membership_request: (n, m) => (m.invite_id ? `/dashboard/demo-invites?invite=${enc(m.invite_id)}` : null),
  ng_bulk_payment_authorisation: (n, m) => (m.payment_id ? `/dashboard/ng-invoices?payment=${enc(m.payment_id)}` : '/dashboard/ng-invoices'),
  new_lead: () => '/dashboard/leads',
  call_request: () => '/dashboard/leads',
  follow_up: () => '/dashboard/leads',
  salon_order_placed: (n, m) => (m.salon_order_id ? `/dashboard/uk-orders?focus=${enc(m.salon_order_id)}` : '/dashboard/uk-orders'),
  // stock_check_notification_deeplink_09oct2026 (CEO order Q8): a stock
  // check notification opens THAT submitted count in the review panel
  // (/dashboard/stock-reps supports ?focus=), never the counting tool.
  stock_update: (n, m) => (m.check_id ? `/dashboard/stock-reps?focus=${enc(m.check_id)}` : null),
};

// Generic record-id scan so a NEW type with any recognisable id cannot
// fall through to a dashboard.
const META_ID_ROUTES = [
  // notif_writer_links_07oct2026 (CEO order): a payment proof ALWAYS opens
  // THAT receipt in Payment Review — first in priority.
  ['proof_id', (id) => `/dashboard/payment-review/${id}`],
  ['receipt_id', (id) => `/dashboard/payment-review/${id}`],
  ['subscription_id', (id) => `/dashboard/subscriptions?focus=${id}`],
  ['change_request_id', () => '/admin/dashboard?tab=change-requests'],
  ['verification_user_id', (id) => `/dashboard/verification?focus=${id}`],
  ['quote_id', (id) => `/dashboard/quotes?focus=${id}`],
  ['order_id', (id) => orderRoute(decodeURIComponent(id))],
  ['invoice_id', (id) => `/dashboard/uk-invoices?focus=${id}`],
  ['fulfilment_id', (id) => `/dashboard/orders?focus=${id}`],
  ['request_id', (id) => `/dashboard/product-requests?focus=${id}`],
  ['enquiry_id', (id) => `/dashboard/salon-pipeline?focus=${id}`],
  ['invite_id', (id) => `/dashboard/demo-invites?invite=${id}`],
  ['registered_user_id', (id) => `/dashboard/salon-pipeline?focus=${id}`],
  ['check_id', (id) => `/dashboard/stock-reps?focus=${id}`],
];

// ceo_instruction_ref: message_record_deeplinks_27sep2026 — a system message
// that names a record opens THAT record; only genuine person-to-person
// messages open a thread. Resolution: meta record id → stored record link →
// order ref extracted from the title (e.g. "Order Approved — RK-7176") →
// thread (P2P fallback).
const MESSAGE_TITLE_REF_RE = /(?:—|–|:)\s*([A-Z]{2,}(?:-[A-Za-z0-9]+)+)\s*$/;
const ORDER_REF_CATEGORIES = new Set([
  'order_update', 'order_assignment', 'order_verification', 'order_approval',
  'delivery_update', 'stock_alert', 'security_alert', 'payment_update',
]);

function resolveMessageRecord(n, m) {
  const meta = m || {};
  for (const [key, build] of META_ID_ROUTES) {
    if (meta[key]) return build(enc(meta[key]));
  }
  const stored = String(n.link || '').trim();
  if (stored && !isDashboardHome(stored) && !stored.startsWith('/dashboard/messages')) return stored;
  if (ORDER_REF_CATEGORIES.has(n.category)) {
    const ref = MESSAGE_TITLE_REF_RE.exec(String(n.title || '').trim());
    if (ref) return orderRoute(ref[1]);
  }
  if (n.category === 'lead_assignment') return '/dashboard/leads';
  return n.message_id ? `/dashboard/messages?open=${enc(n.message_id)}` : null;
}

export function resolveDestination(notif) {
  const meta = notif.meta || {};
  const typeRoute = TYPE_RECORD_ROUTES[notif.type];
  if (typeRoute) {
    const r = typeRoute(notif, meta);
    if (r) return r;
  }
  // notif_writer_links_07oct2026: a proof/receipt id beats a stored PAGE
  // link — old proof notifications stored the bare review page.
  if (meta.proof_id) return `/dashboard/payment-review/${enc(meta.proof_id)}`;
  if (meta.receipt_id) return `/dashboard/payment-review/${enc(meta.receipt_id)}`;
  const stored = String(notif.link || '').trim();
  if (stored && !isDashboardHome(stored)) return stored;
  for (const [key, build] of META_ID_ROUTES) {
    if (meta[key]) return build(enc(meta[key]));
  }
  if (notif.media_id) return `/dashboard/media-review?media=${enc(notif.media_id)}`;
  return null;
}

function timeAgo(dateStr) {
  // ceo_instruction_ref: notification_exact_dates_25sep2026 — actual date
  // and time on every row, never relative.
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function NotificationBell({ testId = 'notification-bell' }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [redOnly, setRedOnly] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-total`, { headers });
      setUnread(res.data.total || 0);
    } catch (err) { /* silent */ }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/unified?limit=30`, { headers });
      setNotifications(res.data.notifications || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchCount();
    intervalRef.current = setInterval(fetchCount, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchCount]);

  useEffect(() => {
    if (panelOpen) fetchNotifications();
  }, [panelOpen, fetchNotifications]);

  const markDone = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.post(`${API}/notifications/mark-done/${id}`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, light: 'green', done: true, read: true } : n));
    } catch { /* noop */ }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/mark-read/${id}`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (err) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (err) { /* silent */ }
  };

  const openNotif = (notif) => {
    if (!notif.read) markRead(notif.id);
    // ceo_instruction_ref: ia_notification_tap_deep_links_15jul2026
    const dest = resolveDestination(notif);
    if (dest) {
      navigate(dest);
      setPanelOpen(false);
      setSelectedNotif(null);
      return;
    }
    setSelectedNotif(notif);
  };

  const navigateTo = (notif) => {
    const route = resolveDestination(notif) || TYPE_ROUTES[notif.type];
    if (route) {
      navigate(route);
      setPanelOpen(false);
      setSelectedNotif(null);
    }
  };

  return (
    <>
      {/* Bell Button */}
      <button
        className="relative p-2 rounded-md hover:bg-black/5 transition-colors"
        onClick={() => setPanelOpen(true)}
        data-testid={testId}
      >
        <Bell className="w-5 h-5 text-foreground/70" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1"
            data-testid="notification-badge-count">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Full-Screen Notification Panel — rendered via portal so a
          transformed/filtered ancestor (e.g. the sidebar) can never
          clip it (sidebar_bell_panel_fix_19oct2026, CEO order). */}
      {panelOpen && createPortal(
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="notification-panel">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setPanelOpen(false); setSelectedNotif(null); }} />

          {/* Slide-in panel */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-slide-in-right">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E3EAF3] bg-[#F8FAFB] shrink-0">
              <div className="flex items-center gap-2.5">
                <Bell className="w-5 h-5 text-[#2B1F27]" />
                <h2 className="font-serif text-lg font-semibold text-[#2B1F27]">Notifications</h2>
                {unread > 0 && (
                  <Badge className="bg-red-500 text-white text-[9px] rounded-full h-5 min-w-5 flex items-center justify-center border-0">
                    {unread}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <Button variant="ghost" size="sm"
                    className="text-[11px] h-7 px-2 text-[#2563EB] hover:text-[#1d4ed8] hover:bg-blue-50 font-medium"
                    onClick={markAllRead} data-testid="mark-all-read-btn">
                    Mark all read
                  </Button>
                )}
                <button onClick={() => { setPanelOpen(false); setSelectedNotif(null); }}
                  className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
                  data-testid="close-notifications">
                  <X className="w-5 h-5 text-[#4A4A4A]" />
                </button>
              </div>
            </div>

            {/* Content Area */}
            {selectedNotif ? (
              /* ── Expanded Single Notification ── */
              <div className="flex-1 flex flex-col overflow-hidden" data-testid="notification-detail">
                {/* Back to list */}
                <button onClick={() => setSelectedNotif(null)}
                  className="flex items-center gap-1.5 px-5 py-3 text-[11px] uppercase tracking-wider text-[#2563EB] hover:bg-blue-50 transition-colors border-b border-[#E3EAF3] shrink-0 font-medium"
                  data-testid="notif-back-to-list">
                  <ArrowRight className="w-3 h-3 rotate-180" /> Back to all notifications
                </button>

                <ScrollArea className="flex-1">
                  <div className="px-5 py-5 space-y-4">
                    {/* Type badge + time */}
                    <div className="flex items-center justify-between">
                      <Badge className={`text-[10px] uppercase tracking-wider ${TYPE_COLORS[selectedNotif.type] || 'text-gray-500'} bg-[#F4F8FC] border-[#E3EAF3]`}>
                        {(() => { const I = TYPE_ICONS[selectedNotif.type] || Bell; return <I className="w-3 h-3 mr-1" />; })()}
                        {selectedNotif.type?.replace(/_/g, ' ') || 'notification'}
                      </Badge>
                      <span className="text-[11px] text-[#64525D] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(selectedNotif.created_at)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-serif text-xl font-semibold text-[#2B1F27] leading-tight">
                      {selectedNotif.title}
                    </h3>

                    {/* Sender */}
                    {selectedNotif.sender_name && (
                      <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                        <div className="w-7 h-7 rounded-full bg-[#E3EAF3] flex items-center justify-center text-[10px] font-semibold text-[#4A4A4A]">
                          {selectedNotif.sender_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <span className="font-medium">{selectedNotif.sender_name}</span>
                      </div>
                    )}

                    {/* Full message body */}
                    <div className="bg-[#F8FAFB] border border-[#E3EAF3] rounded-md p-4">
                      <p className="text-sm text-[#2B1F27] leading-relaxed whitespace-pre-wrap">
                        {selectedNotif.content || selectedNotif.body || 'No additional details.'}
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="space-y-2 pt-2">
                      {(resolveDestination(selectedNotif) || TYPE_ROUTES[selectedNotif.type]) && (
                        <Button className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white gap-2 h-10"
                          onClick={() => navigateTo(selectedNotif)}
                          data-testid="notif-action-btn">
                          <ExternalLink className="w-4 h-4" />
                          {selectedNotif.type === 'message' ? ((resolveDestination(selectedNotif) || '').startsWith('/dashboard/messages') ? 'Open Messages' : 'Open Record') :
                           selectedNotif.type === 'order' || selectedNotif.type === 'fulfilment_assigned' ? 'View Orders' :
                           selectedNotif.type === 'stock_update' ? 'View Stock' :
                           selectedNotif.type === 'payment' ? 'View Orders' : 'View Details'}
                        </Button>
                      )}
                      {!selectedNotif.read && (
                        <Button variant="outline" className="w-full gap-2 h-10 border-[#E3EAF3] text-[#4A4A4A]"
                          onClick={() => { markRead(selectedNotif.id); setSelectedNotif({ ...selectedNotif, read: true }); }}
                          data-testid="notif-mark-read-btn">
                          <Eye className="w-4 h-4" /> Mark as read
                        </Button>
                      )}
                    </div>
                  </div>
                </ScrollArea>
              </div>
            ) : (
              /* ── Notification List ── */
              <ScrollArea className="flex-1" data-testid="notification-list">
                <div className="flex items-center justify-between gap-2 px-5 py-2 border-b border-[#E3EAF3]/60">
                  <span className="text-[10px] text-[#64525D]">Red needs action · Amber unresolved · Green dealt with</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setRedOnly(v => !v); }}
                    className={`text-[10px] font-semibold px-2 py-1 rounded-full border shrink-0 ${redOnly ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-600 border-red-300'}`}
                    data-testid="bell-red-only-filter">RED only</button>
                </div>
                {loading && notifications.length === 0 ? (
                  <div className="py-12 text-center text-sm text-[#64525D]">Loading...</div>
                ) : notifications.length === 0 ? (
                  <div className="py-16 text-center">
                    <Bell className="w-10 h-10 text-[#E3EAF3] mx-auto mb-3" />
                    <p className="text-sm text-[#64525D] font-medium">No notifications yet</p>
                    <p className="text-xs text-[#AAA] mt-1">You'll see updates here when something happens</p>
                  </div>
                ) : (
                  <div>
                    {redOnly && notifications.filter(n => n.light === 'red').length === 0 && (
                      <div className="py-8 text-center text-xs text-[#64525D]" data-testid="bell-no-red">Nothing red — no unactioned items</div>
                    )}
                    {(redOnly ? notifications.filter(n => n.light === 'red') : notifications).map((notif) => {
                      const Icon = TYPE_ICONS[notif.type] || Bell;
                      const colorClass = TYPE_COLORS[notif.type] || 'text-gray-500';
                      return (
                        <div key={notif.id}
                          className={`flex items-start gap-3 px-5 py-4 cursor-pointer hover:bg-[#F4F8FC] transition-colors border-b border-[#E3EAF3]/60 ${!notif.read ? 'bg-blue-50/40' : ''}`}
                          onClick={() => openNotif(notif)}
                          data-testid={`notif-item-${notif.id}`}>
                          <div className={`mt-0.5 shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${!notif.read ? 'bg-[#2563EB]/10' : 'bg-[#F4F8FC]'}`}>
                            <Icon className={`w-4 h-4 ${!notif.read ? 'text-[#2563EB]' : colorClass}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {!notif.read && !notif.resolved && (
                                <div title="Unread"
                                  className="w-2.5 h-2.5 rounded-full shrink-0 bg-emerald-500"
                                  data-testid={`notif-light-${notif.id}`} data-light="unread" />
                              )}
                              <span className={`text-sm ${!notif.read ? 'font-semibold text-[#2B1F27]' : 'text-[#4A4A4A]'} line-clamp-1`}>
                                {notif.title}
                              </span>
                              {notif.action_needed && (
                                <span className="text-[9px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-400 px-1.5 py-0.5 rounded-sm shrink-0"
                                  data-testid={`bell-action-needed-${notif.id}`}>Action needed</span>
                              )}
                            </div>
                            <p className="text-xs text-[#64525D] mt-1 line-clamp-2 leading-relaxed">{notif.content}</p>
                            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#AAA]">
                              {notif.sender_name && <span className="font-medium">{notif.sender_name}</span>}
                              <span>{timeAgo(notif.created_at)}</span>
                              {notif.can_mark_done && notif.light !== 'green' && (
                                <button type="button" onClick={(e) => markDone(notif.id, e)}
                                  className="text-[10px] font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50 px-2 py-0.5 rounded-full"
                                  data-testid={`notif-mark-done-${notif.id}`}>Mark Done</button>
                              )}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#CCC] mt-2 shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            )}

            {/* Footer */}
            {!selectedNotif && (
              <div className="border-t border-[#E3EAF3] p-3 bg-[#F8FAFB] shrink-0">
                <Button variant="ghost"
                  className="w-full text-sm h-9 text-[#2563EB] hover:text-[#1d4ed8] hover:bg-blue-50 font-medium"
                  onClick={() => { navigate('/dashboard/messages'); setPanelOpen(false); }}
                  data-testid="view-all-notifications">
                  View all messages
                </Button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
